import axios from 'axios';
import toast from 'react-hot-toast';
import { retryAfterSeconds, waitMessage } from './rateLimit';

// Create axios instance with default config
const api = axios.create({
  // Use explicit base URL for both development and production
  // This is more reliable than relying on the proxy
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:9000',
  timeout: 60000,
  // Send the httpOnly session cookie. Required because the SPA and the API are on
  // different origins (localhost:3000 -> :9000, hrms -> backend-hrms), and the browser
  // omits credentials on cross-origin XHR unless asked. The server's CORS config
  // already sets credentials: true against a strict origin allowlist.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// No request interceptor for auth: the session travels as an httpOnly cookie that
// JavaScript cannot read, which is the point - it removes the XSS-readable token that
// used to sit in localStorage.

// Routes that render without a session, and so must survive a 401.
//
// AuthProvider asks GET /api/auth/profile on mount no matter which route is showing,
// and on a public page the 401 that comes back is the normal "not signed in" answer,
// not a session that just died. Redirecting on it would bounce a visitor off a page
// they are entitled to read - which is how /terms and /privacy silently rendered the
// login page instead of themselves. Keep this in step with the routes registered
// outside ProtectedRoute in App.js.
const PUBLIC_PATHS = ['/login', '/terms', '/privacy'];

const onPublicPath = () =>
  PUBLIC_PATHS.includes(window.location.pathname.replace(/\/+$/, '') || '/');

// One id for every rate-limit toast, so a page that fires several requests at once
// shows one message rather than a stack of identical ones.
const RATE_LIMIT_TOAST_ID = 'rate-limited';

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.response?.data || error.message);
    console.error('🔗 Request URL:', error.config?.url);
    console.error('📊 Response Status:', error.response?.status);
    console.error('📋 Response Headers:', error.response?.headers);
    
    // Handle 401 errors (unauthorized). The cookie is httpOnly so there is nothing to
    // clear client-side; the server already expired or cleared it. Don't bounce off a
    // public page: on /login a failed login would redirect on top of itself, and on the
    // legal pages there was never a session to lose.
    if (error.response?.status === 401 && !onPublicPath()) {
      window.location.href = '/login';
    }

    // Handle ONBOARDING_REQUIRED — nudge the user to the onboarding form
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'ONBOARDING_REQUIRED' &&
      window.location.pathname !== '/onboarding'
    ) {
      toast.error('Please complete onboarding before using this feature.');
      window.location.href = '/onboarding';
    }

    // Rate limited. Nothing retries by itself: each automatic retry spends budget the
    // person needs once the limit resets and adds load to a struggling server, so the
    // toast says how long to hold off and leaves the retry to them. Not on a public
    // page, where /login's error box shows the limiter's message, and not when the
    // request opted out with `explainsOwnRateLimit: true` because its caller explains
    // the 429 itself. The opt-out is per request, not per URL: refreshProfile reads the
    // same endpoint as the session check and explains nothing.
    if (
      error.response?.status === 429 &&
      !onPublicPath() &&
      !error.config?.explainsOwnRateLimit
    ) {
      toast.error(`Too many requests. ${waitMessage(retryAfterSeconds(error))}`, { id: RATE_LIMIT_TOAST_ID });
    }

    return Promise.reject(error);
  }
);

export default api; 